import { WidgetSource, WidgetTilesetSource } from '@carto/api-client'
import { SpatialDataType } from '../types'

// WidgetSource instances encapsulate Web Worker state, updated periodically
// as tiles are loaded. If stored in React hook or component state, we wouldn't
// be able to access them in WidgetSagas to set tile data. If stored in Redux
// state, the Web Worker references and large tile data would be problematic.
// Instead, we keep a module-local cache.
//
// See: features/builder/hooks/widgetHooks.ts

// REDUX HELPERS

export async function updateWidgetSource(datasetId: string, widgetSource: WidgetSource): Promise<void> {
  _setWidgetSourceInternal(datasetId, widgetSource)
}

export async function removeWidgetSource(datasetId: string): Promise<void> {
  _removeWidgetSourceInternal(datasetId)
}

/** For unit testing only. */
export async function clearWidgetSources(): Promise<void> {
  _clearWidgetSourcesInternal()
}

/**
 * Loads viewport tiles into Web Workers for processing. Tiles cannot be queried
 * until clipped and processed with {@link extractWidgetSourceTileFeatures}..
 */
export async function loadWidgetSourceTiles(datasetId: string, tiles: unknown[]): Promise<void> {
  const widgetSource = _getWidgetSourceInternal(datasetId) as WidgetTilesetSource
  widgetSource.loadTiles(tiles)
}

type ExtractWidgetSourceTileFeaturesOptions = {
  uniqueIdProperty?: string
  spatialDataType?: SpatialDataType
  spatialDataColumn?: string
  storeGeometry: boolean
}

/**
 * Extracts tile features for the given dataset ID, after tiles have been loaded
 * into Web Worker memory by {@link loadWidgetSourceTiles}.
 */
export async function setTileFeatureExtractOptions(
  datasetId: string,
  options: ExtractWidgetSourceTileFeaturesOptions
): Promise<void> {
  const widgetSource = _getWidgetSourceInternal(datasetId) as WidgetTilesetSource
  widgetSource.setTileFeatureExtractOptions(options)
}

// INTERNAL HELPERS

const cache = new Map<string, WidgetSource>()

// NOTICE: Exported for useWidgetSource() hook only.
export function _getWidgetSourceInternal(datasetId: string): WidgetSource | undefined {
  return cache.get(datasetId)
}

function _setWidgetSourceInternal(datasetId: string, widgetSource: WidgetSource): void {
  cache.get(datasetId)?.destroy()
  cache.set(datasetId, widgetSource)
}

function _removeWidgetSourceInternal(datasetId: string): void {
  cache.get(datasetId)?.destroy()
  cache.delete(datasetId)
}

/** For unit testing only. */
function _clearWidgetSourcesInternal(): void {
  for (const widgetSource of cache.values()) {
    widgetSource.destroy()
  }
  cache.clear()
}
